'use client';

import type { MouseEvent } from 'react';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { ArrowLeft, Close } from '@zoomit/icons';
import { Box, Flex, Image } from '@zoomit/primitives';
import type { BaseImageType} from '@zoomit/web-kit';
import { isMobile, OriginURL } from '@zoomit/web-kit';
import { Anchor, Button, cssVar, Hr, theme, Typography } from '@zoomit/zoomit-ds';

import { useOrigin } from '../../providers/OriginProvider';

type Props = {
  image: BaseImageType;
  title: string;
  onClose: () => void;
  onSetNotify: () => void;
};

const PriceNotifyModalContent = (props: Props) => {
  const { image, title, onClose, onSetNotify } = props;
  const { user } = useAuth();

  const handleSetNotify = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onSetNotify();
  };

  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  const { baseMediaUrl } = useOrigin();

  return isMobile ? (
    <>
      <Box backgroundColor={cssVar(theme.card)} spacing={{ px: 'x11', py: 'x7' }}>
        <Flex flexDirection="column" alignItems="center" gap={1.6}>
          <Flex justifyContent="center">
            <ImageContainer>
              <Image
                src={`${baseMediaUrl}/${image.id}`}
                fill
                alt={image.alt}
                placeholder="blur"
                blurDataURL={image.preview}
                style={{
                  objectFit: 'contain',
                }}
              />
            </ImageContainer>
          </Flex>
          <Typography fontSize={1.3} fontWeight="bold" className="fa" textAlign="right">
            {title}
          </Typography>
          <Typography fontSize={1.4} lineHeight={1.6}>
            با فعال کردن این قابلیت، زومیت شما را از موجود شدن و تغییرات قیمت این محصول مطلع خواهد کرد.
          </Typography>
          <Hr />
        </Flex>
        <Anchor
          href={`${OriginURL}/profile/${user?.profile.sub}/settings/?form=notificationSettings`}
          leftIcon={<ArrowLeft className="!fill-primary2" />}
          variant="text"
          size={'fit'}
          spacing={{ pr: 'x0', mt: 'x4' }}
          style={{ justifySelf: 'flex-start' }}
        >
          مدیریت همه اعلان‌های قیمت
        </Anchor>
      </Box>
      <ModalBottom gap={2} spacing={{ px: 'x6', py: 'x3' }} justifyContent="center" alignItems="center">
        <Flex gap={1.6}>
          <Button onClick={handleSetNotify} type="submit">
            فعال کن
          </Button>
          <Button variant="outline" onClick={handleClose}>
            انصراف
          </Button>
        </Flex>
      </ModalBottom>
    </>
  ) : (
    <>
      <Box backgroundColor={cssVar(theme.card)} spacing={{ py: 'x6', pl: 'x6' }}>
        <Flex alignItems="center" justifyContent="space-between">
          <Title>
            <Typography fontSize={1.6} fontWeight="bold">
              دریافت اعلانیه تغییرات قیمت
            </Typography>
          </Title>
          <Close size="lg" onClick={onClose} className={'cursor-pointer'} />
        </Flex>
      </Box>
      <Box backgroundColor={cssVar(theme.card)} spacing={{ p: isMobile ? 'x12' : 'x6' }}>
        <Flex flexDirection="column" gap={2.4}>
          <Flex alignItems="center" gap={0.8} flexWrap="nowrap">
            <ImageContainer>
              <Image
                src={`${baseMediaUrl}/${image.id}`}
                fill
                alt={image.alt}
                placeholder="blur"
                blurDataURL={image.preview}
                style={{
                  objectFit: 'contain',
                }}
              />
            </ImageContainer>
            <Typography fontSize={1.3} fontWeight="bold">
              {title}
            </Typography>
          </Flex>
          <Typography fontSize={1.4} lineHeight={1.6}>
            با فعال کردن این قابلیت، زومیت شما را از موجود شدن و تغییرات قیمت این محصول مطلع خواهد کرد.
          </Typography>
          <Hr />
          <Anchor
            href={`${OriginURL}/profile/${user?.profile.sub}/settings/?form=notificationSettings`}
            leftIcon={<ArrowLeft className="!fill-primary2" />}
            variant="text"
            size={'fit'}
            spacing={{ pr: 'x0' }}
          >
            مدیریت همه اعلان‌های قیمت
          </Anchor>
        </Flex>
      </Box>
      <ModalBottom gap={2} spacing={{ px: 'x6', py: 'x3' }}>
        <Flex gap={1.6}>
          <Button onClick={handleSetNotify} type="submit" color="#32323c">
            فعال کن
          </Button>
          <Button variant="outline" onClick={handleClose}>
            انصراف
          </Button>
        </Flex>
      </ModalBottom>
    </>
  );
};

const ModalBottom = styled(Flex)`
  background-color: ${cssVar(theme.background1)};
`;

const ImageContainer = styled.div`
  aspect-ratio: 3/2;
  position: relative;
  height: 64px;
`;

const Title = styled.div`
  position: relative;
  margin-right: 1.2rem;

  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: 2.4rem;
    top: calc(50% - 1.2rem);
    right: -1.2rem;
    background-color: var(${theme.primary1});
  }
`;

export default PriceNotifyModalContent;
